import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import withWidth from '@material-ui/core/withWidth';
import { withStyles } from '@material-ui/core/styles';

import { getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import NoteCategoriesTable from 'components/settings/NoteCategoriesTable';
import NoteCategoryCreateEditDialog from 'components/settings/NoteCategoryCreateEditDialog';
import NoteCategoryReassignDialog from 'components/settings/NoteCategoryReassignDialog';
import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';

import {
  fetchNoteCategories,
  clearNoteCategories,
} from 'redux/modules/noteCategories';
import { addRouterParamsPageSettings } from 'redux/modules/routerParams';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const styles = theme => ({
  filter: {
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: -8,
      right: -8,
      bottom: -24,
      backgroundColor: theme.palette.grey[100],
      [theme.breakpoints.down('sm')]: {
        bottom: -8,
      },
    },
  },
});

class MedicalSourcesNoteCategories extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      initialValues: {},
      snackbarOpen: false,
      snackbarMessage: '',
      reassignNoteCategory: null,
      reassignDialogOpen: false,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onNoteCategoryDeleteFail = this.onNoteCategoryDeleteFail.bind(this);
    this.onSnackbarClose = this.onSnackbarClose.bind(this);
    this.onNoteCategoryReassign = this.onNoteCategoryReassign.bind(this);
    this.onReassignDialogClose = this.onReassignDialogClose.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearNoteCategories();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters } = this.props;
    const filtersChanged = nextProps.filters.Name !== filters.Name;
    if (filtersChanged) {
      this.onRequest();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps) {
    const { filters } = this.props;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Name'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (filtersChanged) {
      this.onRequest();
    }
  }

  onRequest() {
    const { actions, filters, limit, offset } = this.props;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: 'contains',
        val: filters[key],
        case_sensitivity: false,
      }))
      : null;
    actions.fetchNoteCategories({
      filters: requestFilters,
      limit: 'Un-limit',
      triggerLoading: true,
    });
  }

  onFilterButtonClick(variant) {
    this.setState({
      createDialogOpen: true,
      initialValues: {
        ...variant === 'Intake' ? {
          'Workflow State ID': 1,
          'Workflow State Name': 'Intake',
          'Selected Workflow State Name': 'Intake',
          'Is Active': true,
        } : {},
        ...variant === 'Delivery' ? {
          'Workflow State ID': 2,
          'Workflow State Name': 'Delivery',
          'Selected Workflow State Name': 'Delivery',
          'Is Active': true,
        } : {},
        ...variant === 'Billing' ? {
          'Workflow State ID': 3,
          'Workflow State Name': 'Billing',
          'Selected Workflow State Name': 'Billing',
          'Is Active': true,
        } : {},
        ...variant === 'Collection' ? {
          'Workflow State ID': 4,
          'Workflow State Name': 'Collection',
          'Selected Workflow State Name': 'Collection',
          'Is Active': true,
        } : {},
        ...variant === 'Paid' ? {
          'Workflow State ID': 5,
          'Workflow State Name': 'Paid',
          'Selected Workflow State Name': 'Paid',
          'Is Active': true,
        } : {},
        ...variant === 'General' ? {
          'Is Active': true,
        } : {},
      },
    });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onSnackbarClose() {
    this.setState({ snackbarOpen: false });
  }

  onNoteCategoryDeleteFail({ error, reassignNoteCategory }) {
    this.setState({
      snackbarOpen: error ? true : false,
      snackbarMessage: error ? error : '',
      ...error && error.indexOf('This notes category already used') !== -1
        ? { reassignNoteCategory } : { reassignNoteCategory: null },
    });
  }

  onNoteCategoryReassign() {
    this.setState({
      reassignDialogOpen: true,
      snackbarOpen: false,
    });
  }

  onReassignDialogClose() {
    this.setState({ reassignDialogOpen: false });
  }

  render() {
    const { noteCategories, loading, width, classes } = this.props;
    const {
      createDialogOpen,
      initialValues,
      reassignDialogOpen,
      snackbarOpen,
      snackbarMessage,
      reassignNoteCategory,
    } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Note Categories
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Note Categories',
              href: '/settings/workspace/note-categories',
            },
            {
              text: 'Document Categories',
              href: '/settings/workspace/document-categories',
            },
            {
              text: 'Workflow Statuses',
              href: '/settings/workspace/workflow-statuses',
            },
            {
              text: 'Claim Statuses',
              href: '/settings/workspace/claim-statuses',
            },
            {
              text: 'Claim Codes',
              href: '/settings/workspace/claim-adjustment-codes',
            },
          ]}
        />
        <Filter
          className={classes.filter}
          form="noteCategoriesFilter"
          placeholder="Search Note Categories"
          top={width === 'xs' || width === 'sm' ? 110 : 126}
          beforeTable
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <NoteCategoriesTable
          items={noteCategories}
          onCreate={this.onFilterButtonClick}
          loading={loading}
          onNoteCategoryDeleteFail={this.onNoteCategoryDeleteFail}
        />
        <NoteCategoryCreateEditDialog
          form="noteCategoryCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
          initialValues={initialValues}
        />
        <NoteCategoryReassignDialog
          open={reassignDialogOpen}
          onClose={this.onReassignDialogClose}
          onSubmitSuccess={() => {
            this.onReassignDialogClose();
          }}
          form="reassignNoteCategory"
          initialValues={{
            'Old Notes Category ID': reassignNoteCategory ? reassignNoteCategory.ID : null,
            'Old Notes Category Name': reassignNoteCategory ? reassignNoteCategory.Name : null,
          }}
        />
        <Snackbar
          open={snackbarOpen}
          variant="warning"
          message={snackbarMessage}
          autoHideDuration={5000}
          resumeHideDuration={0}
          onClose={this.onSnackbarClose}
          {...reassignNoteCategory ? {
            action: (
              <Button
                variant="raised"
                color="primary"
                onClick={this.onNoteCategoryReassign}
              >
                Reassign Category
              </Button>
            ),
          } : {}}
        />
      </Layout>
    );
  }
}

MedicalSourcesNoteCategories.propTypes = {
  noteCategories: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    Name: PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchNoteCategories: PropTypes.func.isRequired,
    clearNoteCategories: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    filter: PropTypes.string.isRequired,
  }).isRequired,
};

MedicalSourcesNoteCategories.defaultProps = {};

const mapStateToProps = (store, ownProps) => {
  const noteCategoriesFilterValues = getFormValues('noteCategoriesFilter')(store);
  return {
    noteCategories: store.noteCategories.data,
    filters: {
      ...noteCategoriesFilterValues
        && noteCategoriesFilterValues.Filter
        ? { Name: noteCategoriesFilterValues.Filter }
        : {},
    },
    loading: store.noteCategories.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchNoteCategories,
    clearNoteCategories,
    addRouterParamsPageSettings,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(withStyles(styles)(MedicalSourcesNoteCategories)),
);



// WEBPACK FOOTER //
// ./src/pages/settings/MedicalSourcesNoteCategories.js